import React, { Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ContentFilterLinkedInView, ContentFilterLinkedInViewProps } from './ContentFilterLinkedIn.view';
import { UseFormReturn, useWatch } from 'react-hook-form';
import {
  ImportSourceArgsGroupInput,
  ImportSourceArgsLinkedInInput,
  LinkedInSearchContentType,
  LinkedInSearchSortType,
  LinkedInSearchType,
  SocialOAuthProvider,
  SocialProfileFields_LinkedInSocialProfile_Fragment
} from '../../../../models/graphql';
import { FormElementValueType } from '../../../../models/basic-objects';
import { SearchLinkedInPostsVariables } from './ContentFilterLinkedIn.apollo';
import { getApolloErrorMessage, remapApolloErrors } from '../../../../helpers/remapApolloErrors';
import { ApolloError } from '@apollo/client';
import { FindContentContext, SearchStepEnum } from '../FindContentProvider';
import { useDidMount } from '../../../../hooks/useDidMount';
import { PostSourceType } from '../../../../constants/find-content';
import { TreeMenuContext } from '../TreeMenu/TreeMenuProvider';

export type ContentFilterLinkedInControllerProps = {
  formInstance: UseFormReturn;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  searchParameters: SearchLinkedInPostsVariables | undefined;
  setSearchParameters: Dispatch<SetStateAction<SearchLinkedInPostsVariables | undefined>>;
  fetchLinkedInPosts: (parameters: SearchLinkedInPostsVariables) => Promise<void>;
  purgeLinkedInCache: () => void;
};

export const ContentFilterLinkedInController = (props: ContentFilterLinkedInControllerProps): JSX.Element => {
  const {
    formInstance,
    fetchLinkedInPosts,
    setShowFilters,
    purgeLinkedInCache,
    searchParameters,
    setSearchParameters
  } = props;
  const [showSaveSourceModal, setShowSaveSourceModal] = useState<boolean>(false);
  const { modalType, selectedMenuItem, setSelectedMenuItem } = useContext(TreeMenuContext);
  const { posts, setError, setSourceType, setSelectedPosts, profiles, setSearchStep, setHasSearched } =
    useContext(FindContentContext);
  const [apolloErrorKey, setApolloErrorKey] = useState<string | undefined>(undefined);
  const typeValue = useWatch({ name: 'type', control: formInstance.control });

  const clearFilterBarForm = useCallback(() => {
    const elementNames = ['contentType', 'sortType', 'term'];
    elementNames.forEach(e => formInstance.resetField(e));

    setSearchStep(SearchStepEnum.ONE);
  }, [formInstance, setSearchStep]);

  const clearLinkedInForm = useCallback(
    (excluded: string) => {
      const elementNames = [...Object.values(LinkedInSearchType), 'contentType', 'sortType', 'term'];

      elementNames
        .filter(n => n !== excluded)
        .forEach(e => {
          formInstance.resetField(e);
        });
    },
    [formInstance]
  );

  const linkedInProfiles = useMemo(() => {
    return profiles.filter(
      p => p.__typename === 'LinkedInSocialProfile'
    ) as SocialProfileFields_LinkedInSocialProfile_Fragment[];
  }, [profiles]);

  const getLinkedInPosts = useCallback(
    (parameters: SearchLinkedInPostsVariables) => {
      purgeLinkedInCache();

      setSearchParameters(parameters);

      fetchLinkedInPosts(parameters)
        .then(() => {
          setShowFilters(true);
          setError(undefined);
        })
        .catch(error => {
          const apolloErrorType = getApolloErrorMessage(error);
          const remappedError = remapApolloErrors(error as ApolloError);

          setApolloErrorKey(apolloErrorType);
          setError(remappedError);

          // we have cases where the cache will presist or re-emerge so we always want it
          // empty if there is an error
          purgeLinkedInCache();
        });
    },
    [fetchLinkedInPosts, purgeLinkedInCache, setError, setSearchParameters, setShowFilters]
  );

  const submitFormHandler = useCallback(
    (value: FormElementValueType) => {
      clearFilterBarForm();

      const profile = linkedInProfiles.find(p => p.id === value);
      const parameters: SearchLinkedInPostsVariables = {
        contentType: formInstance.getValues('content') ?? LinkedInSearchContentType.ALL,
        sortType: formInstance.getValues('sort') ?? LinkedInSearchSortType.LATEST,
        searchType: formInstance.getValues('type') ?? LinkedInSearchType.PROFILE,
        socialProfileId: `${value}`,
        externalId: profile?.linkedInId || '',
        term: ''
      };

      getLinkedInPosts(parameters);
    },
    [clearFilterBarForm, formInstance, getLinkedInPosts, linkedInProfiles]
  );

  const filterSearchHandler = useCallback(
    (resetFilterBar?: boolean, ignoreStepIncrement?: boolean) => {
      if (resetFilterBar) {
        clearFilterBarForm();
      }

      if (searchParameters) {
        purgeLinkedInCache();
        setError(undefined);

        if (!ignoreStepIncrement) {
          setSearchStep(SearchStepEnum.TWO);
        }

        const parameters: SearchLinkedInPostsVariables = {
          ...searchParameters,
          sortType: formInstance.getValues('sortType') ?? LinkedInSearchSortType.LATEST,
          contentType: formInstance.getValues('contentType') ?? LinkedInSearchContentType.ALL,
          term: formInstance.getValues('term') ?? ''
        };

        getLinkedInPosts(parameters);
      }
    },
    [searchParameters, clearFilterBarForm, purgeLinkedInCache, setError, formInstance, getLinkedInPosts, setSearchStep]
  );

  const getLinkedInInputArgs = useCallback((): ImportSourceArgsGroupInput => {
    const { type: searchType, sortType, term, contentType } = formInstance.getValues();
    const socialProfileId = searchParameters?.socialProfileId || '';
    const externalId = searchParameters?.externalId || '';

    const thirdPartyProfileDisplayName = linkedInProfiles.find(p => p.linkedInId === externalId)?.displayName;

    const requiredArgs: ImportSourceArgsLinkedInInput = {
      contentType,
      sortType,
      searchType,
      socialProfileId,
      externalId,
      term,
      thirdPartyProfileDisplayName
    };

    const args = {
      ...requiredArgs,
      socialProfileId: searchParameters?.socialProfileId,
      externalId: searchParameters?.externalId
    };

    return { linkedIn: args };
  }, [formInstance, linkedInProfiles, searchParameters?.externalId, searchParameters?.socialProfileId]);

  // Prefill with SavedSource
  useEffect(() => {
    if (
      !modalType &&
      selectedMenuItem &&
      selectedMenuItem.__typename === 'ImportSource' &&
      selectedMenuItem.args.__typename === 'ImportSourceArgsLinkedIn'
    ) {
      purgeLinkedInCache();

      const { args } = selectedMenuItem;

      setError(undefined);

      formInstance.reset({
        ...args,
        platform: SocialOAuthProvider.LINKEDIN,
        type: args.linkedInSearchType
      });

      const parameters: SearchLinkedInPostsVariables = {
        socialProfileId: String(args.socialProfileId),
        externalId: String(args.externalId),
        searchType: args.linkedInSearchType,
        contentType: args.linkedInContentType,
        sortType: args.linkedInSortType,
        term: args.term ?? ''
      };

      fetchLinkedInPosts(parameters)
        .then(() => {
          setShowFilters(true);
          setSelectedMenuItem(undefined);
        })
        .catch(error => {
          setError(remapApolloErrors(error as ApolloError));
        })
        .finally(() => {
          formInstance.setValue('type', LinkedInSearchType.PROFILE);
          formInstance.setValue('contentType', String(args.linkedInContentType));
          formInstance.setValue('sortType', String(args.linkedInSortType));
          formInstance.setValue(LinkedInSearchType.PROFILE, String(args.socialProfileId));

          setSearchParameters(parameters);
        });
    }
  }, [
    fetchLinkedInPosts,
    formInstance,
    modalType,
    purgeLinkedInCache,
    selectedMenuItem,
    setError,
    setSearchParameters,
    setSelectedMenuItem,
    setShowFilters
  ]);

  useEffect(() => {
    setSearchParameters(undefined);
    clearLinkedInForm(typeValue);
    setSearchStep(SearchStepEnum.ONE);
    purgeLinkedInCache();
    setError(undefined);
    setShowFilters(false);
  }, [
    clearLinkedInForm,
    formInstance,
    purgeLinkedInCache,
    setError,
    setSearchParameters,
    setSearchStep,
    setShowFilters,
    typeValue
  ]);

  useDidMount(() => {
    setError(undefined);
    setHasSearched(false);
    setSourceType(PostSourceType.LinkedInSocialProfile);

    if (linkedInProfiles.length < 1) {
      formInstance.setValue('type', -1);
    } else {
      formInstance.setValue('type', LinkedInSearchType.PROFILE);
    }

    return () => {
      setSourceType(undefined);
      clearLinkedInForm('');
      setSelectedPosts([]);
      purgeLinkedInCache();
    };
  });

  const viewProps: ContentFilterLinkedInViewProps = {
    ...props,
    submitFormHandler,
    hasPosts: posts.length > 0,
    filterSearchHandler,
    showSaveSourceModal,
    setShowSaveSourceModal,
    getLinkedInInputArgs,
    linkedInProfiles,
    apolloErrorKey
  };

  return <ContentFilterLinkedInView {...viewProps} />;
};
